import React, { Component, useCallback, useRef } from "react";
import { FormattedMessage, injectIntl, type IntlShape, type MessageDescriptor, useIntl } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";
import { NavLink, type RouteComponentProps } from "react-router-dom";
import Select from "react-select";
import makeAnimated from "react-select/animated";

import { getDoctorsPays, getDoctorsTasks, getPatientsData } from "~/actions/dashboard";
import { getAllShipments } from "~/actions/deliveries";
import { getToken } from "~/actions/get_token";
import precise from "~/assets/img/precise.svg";
import { isNone } from "~/common/assert";
import { STATUSES } from "~/common/constants";
import { isPlanApprovalStatus } from "~/common/courses";
import { remoteLog } from "~/common/logging";
import { FormatDate } from "~/components/common/FormatDate";
import { FormatNumber } from "~/components/common/FormatNumber";
import { PersonName } from "~/components/common/PersonName";
import { notificationCustomStyle } from "~/components/notifications/notification_styles";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Layout } from "~/components/ui/layout";
import Pagination from "~/components/ui/pagination";
import { PatientStatusBadge } from "~/components/ui/patient-status-badge";
import { Portlet, PortletTitle } from "~/components/ui/portlet";
import { Table, TableCell, TableEmpty, TableHead, TableLoading, TableRow } from "~/components/ui/table";
import { getPlanLink } from "~/config";
import { setDocumentTitle } from "~/hooks/use-document-title";
import type { TPatientList } from "~/reducers/dashboard";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";

import PatientBonuses from "./patient_bonuses";
import { CourseName } from "./patient_show/patient-card/patient-card-course";
import { buildStatusFilter, type TSelectOption } from "./patients-status-filter";

const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    patients: state.patients,
    spinner: state.spinner,
    token: state.token,
  };
};

const mapDispatchToProps = {
  getData: getPatientsData,
  getTasks: getDoctorsTasks,
  getPays: getDoctorsPays,
  getShipments: getAllShipments,
  getToken,
};

const animatedComponents = makeAnimated();

type PatientsListNewProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps;

type PatientsListNewState = {
  filteredPatients: TPatientList[];
  displayPayments: boolean;
  payment_alert: boolean;
  sort: {
    order: "desc";
    name: "patient_id";
  };
  status: string | false;
  ordering: "patient_id" | "-patient_id" | "patient_name" | "-patient_name";
  search: string | false;
  page: number;
  perPage: number;
  selectedOptions: TSelectOption[];
};

function selectOptionsEqual(a: TSelectOption[], b: TSelectOption[]): boolean {
  if (a.length !== b.length) {
    return false;
  }
  return a.every((option, index) => option.value === b[index]?.value);
}

class PatientsListNew extends Component<PatientsListNewProps, PatientsListNewState> {
  // Read localStorage at call time — a static field would bake whatever another test (or the redesign
  // patients page) last wrote into `perPage` at module import, and every later mount would reuse it.
  static readPerPage(): number {
    return parseInt(window.localStorage.getItem("perPage") ?? "", 10) || 100;
  }

  // Class components use an instance-held timer for search debounce.
  searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: PatientsListNewProps) {
    super(props);
    this.state = {
      filteredPatients: props.patients.patients,
      displayPayments: false,
      payment_alert: false,
      sort: {
        order: "desc",
        name: "patient_id",
      },
      status: false,
      ordering: "-patient_id",
      search: false,
      page: this.getPageFromURL() ?? 1,
      perPage: PatientsListNew.readPerPage(),
      selectedOptions: [],
    };
  }

  getPageFromURL() {
    const searchParams = new URLSearchParams(this.props.location.search);
    const maybePageAsString = searchParams.get("page");

    if (maybePageAsString) {
      const pageAsNumber = Number(maybePageAsString);
      if (!Number.isNaN(pageAsNumber) && pageAsNumber > 0) {
        return pageAsNumber;
      }
    }

    return null;
  }

  setPageInURL(newPage: number) {
    const searchParams = new URLSearchParams(this.props.location.search);
    searchParams.set("page", String(newPage));
    this.props.history.replace({ search: searchParams.toString() });
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patients_list");
  }

  componentDidMount() {
    setDocumentTitle(this.props.intl.formatMessage({ id: "pat_table.block_head" }));
    window.scrollTo(0, 0);
    this.setPageInURL(this.state.page);

    void this.props.getToken();
    void this.props.getPays();
    void this.props.getTasks();
    void this.props.getShipments();
    void this.props.getData(this.state.status, this.state.ordering, this.state.search, this.state.page, this.state.perPage);
  }

  componentDidUpdate(prevProps: PatientsListNewProps, prevState: PatientsListNewState) {
    if (prevProps.user.account_id && !this.props.user.account_id) {
      this.props.history.push("/");
    }
    if (prevProps.patients.patients !== this.props.patients.patients && this.state.filteredPatients !== this.props.patients.patients) {
      this.setState({ filteredPatients: this.props.patients.patients });
    }
    if (this.props.patients.payment_alert && !this.state.payment_alert) {
      this.setState({ payment_alert: true });
    }
    if (this.state.ordering != prevState.ordering || this.state.page != prevState.page || this.state.perPage != prevState.perPage) {
      void this.props.getData(this.state.status, this.state.ordering, this.state.search, this.state.page, this.state.perPage);
    }
  }

  componentWillUnmount() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
  }

  // Track `state.search` immediately so explicit submission uses the current value, then debounce fetching.
  // The request runs 300ms after the last keystroke, matching
  // the redesign pages' `useDebouncedCallback`. Clearing the field still fetches instantly.
  handleFilterChange(event: React.KeyboardEvent<HTMLInputElement>) {
    const string = event.currentTarget.value.trim().toLowerCase();

    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);

    if (event.currentTarget.value.length == 0) {
      this.setState({ search: false });

      void this.props.getData(this.state.status, this.state.ordering, false, this.state.page, this.state.perPage);
    } else {
      this.setState({ search: string });

      this.searchDebounceTimer = setTimeout(() => {
        void this.props.getData(this.state.status, this.state.ordering, string, this.state.page, this.state.perPage);
      }, 300);
    }
  }

  handleEnterSearch(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key == "Enter") {
      // Enter still works (an immediate submit some doctors reach for out of habit) — just no longer
      // REQUIRED. Cancel the pending debounced fetch so it can't re-fire the same term a moment later.
      if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);

      const value = event.currentTarget.value.trim();
      // Use the just-computed value, NOT this.state.search: setState is async, so
      // reading state in the same tick submits the previous term.
      // Lowercase to match handleFilterChange, so Enter and the keyUp path agree.
      const nextSearch = value === "" ? false : value.toLowerCase();

      this.setState({ search: nextSearch });

      void this.props.getData(this.state.status, this.state.ordering, nextSearch, this.state.page, this.state.perPage);
    }
  }

  onSelect = (options: TSelectOption[]) => {
    if (selectOptionsEqual(options, this.state.selectedOptions)) {
      return;
    }

    const status = buildStatusFilter(options);
    if (status === false) {
      this.clear();
      return;
    }

    this.setState({
      selectedOptions: options,
      status,
    });
  };

  submit() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.setState({ filteredPatients: [], page: 1 });

    void this.props.getData(this.state.status, this.state.ordering, this.state.search, this.state.page, this.state.perPage);
  }

  clear() {
    if (this.searchDebounceTimer) clearTimeout(this.searchDebounceTimer);
    this.setState({
      status: false,
      ordering: "-patient_id",
      search: false,
      page: 1,
      perPage: PatientsListNew.readPerPage(),
      selectedOptions: [],
    });

    void this.props.getData();
  }

  handleOrderingChange(name: "patient_id" | "patient_name") {
    this.setState({ ordering: this.state.ordering == name ? `-${name}` : name });
  }

  handlePageChange(newPage: number) {
    this.setState({ page: newPage });
    this.setPageInURL(newPage);
    window.scrollTo(0, 0);
  }

  handleSelectPerPage(newPerPage: number) {
    this.setState({ perPage: newPerPage });
    window.localStorage.setItem("perPage", `${newPerPage}`);
  }

  render() {
    const { token, spinner, patients } = this.props;
    const { selectedOptions, filteredPatients, ordering, page, perPage, payment_alert, displayPayments } = this.state;

    return (
      <Layout notificationStyle={notificationCustomStyle}>
        <Portlet as="main" testId="patients-page">
          <PortletTitle iconClassName="icon-users" id="patients-table-block-title" testId="patients-page-title">
            <FormattedMessage id="pat_table.block_head" />
          </PortletTitle>

          <PatientBonuses />

          <div className="portlet-body">
            <div id="doctor_pacients_table_wrapper" className="dataTables_wrapper no-footer">
              <PatientsFilters
                selectedOptions={selectedOptions}
                onOptionsChange={this.onSelect}
                onSearchKeyUp={(e) => this.handleFilterChange(e)}
                onSearchKeyDown={(e) => this.handleEnterSearch(e)}
                onSubmitClick={() => this.submit()}
                onClearClick={() => this.clear()}
              />

              <div className="row" style={{ paddingTop: "10px", paddingBottom: "10px" }}>
                <div className="col-lg-8 col-md-8 col-sm-8 col-xs-12">
                  <Button onClick={() => this.setState({ displayPayments: !displayPayments })} variant={payment_alert ? "danger" : "default"}>
                    <FormattedMessage id={displayPayments ? "general.payment.close" : "general.payment.show"} />
                  </Button>
                </div>
              </div>

              <PatientsTable
                ordering={ordering}
                onOrderingChange={(value) => this.handleOrderingChange(value)}
                isLoading={spinner == true}
                patients={filteredPatients}
                displayPayments={displayPayments}
                token={token}
              />

              <div className="row">
                <div className="col-md-5">
                  <Pagination totalItemsCount={patients.total} onChange={(value) => this.handlePageChange(value)} activePage={page} itemsCountPerPage={perPage} pageRangeDisplayed={3} />
                </div>

                <PageSizes perPage={perPage} onSelectPerPage={(value) => this.handleSelectPerPage(value)} />
              </div>
            </div>
          </div>
        </Portlet>
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientsListNew));

function PatientsFilters({
  selectedOptions,
  onOptionsChange,
  onSearchKeyUp,
  onSearchKeyDown,
  onSubmitClick,
  onClearClick,
}: {
  selectedOptions: TSelectOption[];
  onOptionsChange(options: TSelectOption[]): void;
  onSearchKeyUp: React.KeyboardEventHandler<HTMLInputElement>;
  onSearchKeyDown: React.KeyboardEventHandler<HTMLInputElement>;
  onSubmitClick(): void;
  onClearClick(): void;
}) {
  const intl = useIntl();
  const inputRef = useRef<HTMLInputElement>(null);
  const formatOptionLabel = useCallback((option: TSelectOption) => intl.formatMessage({ id: option.label }), [intl]);
  const handleStatusChange = useCallback(
    (options: readonly TSelectOption[] | null) => {
      onOptionsChange((options ?? []) as TSelectOption[]);
    },
    [onOptionsChange],
  );

  return (
    <div className="row">
      <div className="col-md-6 col-sm-12" data-testid="patients-status-filter">
        {/* @ts-expect-error TSelectOption value can be a number, it works. */}
        <Select<TSelectOption>
          value={selectedOptions}
          closeMenuOnSelect={false}
          components={animatedComponents}
          isMulti
          options={STATUSES}
          getOptionLabel={formatOptionLabel}
          getOptionValue={(option) => option.value}
          onChange={handleStatusChange}
          placeholder={<FormattedMessage id="table.status" />}
        />
      </div>

      <div className="col-md-3 col-xs-7">
        <Input
          ref={inputRef}
          name="search"
          maxLength={128}
          onKeyUp={onSearchKeyUp}
          type="search"
          aria-controls="doctor_pacients_table"
          placeholder={intl.formatMessage({ id: "table.search" })}
          style={{ height: "38px" }}
          onKeyDown={onSearchKeyDown}
        />
      </div>

      <div className="col-md-1 col-xs-1">
        <button
          style={{ backgroundColor: "#efefef", marginLeft: "-16px" }}
          type="submit"
          onClick={onSubmitClick}
          className="btn btn-circle hover:tw-bg-[#e0e0e0]"
          title={intl.formatMessage({ id: "table.search" })}
        >
          <span className="glyphicon glyphicon-search"></span>
        </button>
      </div>

      <div className="col-md-1 col-xs-2">
        <button
          style={{ backgroundColor: "#efefef" }}
          type="reset"
          onClick={() => {
            onClearClick();

            if (inputRef.current) {
              inputRef.current.value = "";
            }
          }}
          className="btn btn-circle hover:tw-bg-[#e0e0e0]"
          title={intl.formatMessage({ id: "table.clear" })}
        >
          <span className="glyphicon glyphicon-refresh"></span>
        </button>
      </div>
    </div>
  );
}

function PatientsTable({
  ordering,
  onOrderingChange,
  isLoading,
  patients,
  displayPayments,
  token,
}: {
  ordering: PatientsListNewState["ordering"];
  onOrderingChange(value: "patient_id" | "patient_name"): void;
  isLoading: boolean;
  patients: TPatientList[];
  displayPayments: boolean;
  token: PatientsListNewProps["token"];
}) {
  const intl = useIntl();

  return (
    <Table id="doctor_pacients_table">
      <thead>
        <TableRow>
          <TableHead sort={ordering == "patient_id" ? "asc" : "desc"} style={{ width: 14 }} onClick={() => onOrderingChange("patient_id")}>
            #
          </TableHead>

          <TableHead sort={ordering == "patient_name" ? "asc" : "desc"} style={{ width: 111 }} onClick={() => onOrderingChange("patient_name")}>
            <span style={{ marginRight: "12px" }}>
              <FormattedMessage id="pat_table.name" />
            </span>
          </TableHead>

          <TableHead style={{ width: 82 }}>
            <FormattedMessage id="HEADER_INSTRUCTIONS" />
          </TableHead>

          <TableHead style={{ width: 150 }}>
            <FormattedMessage id="pat_table.delivered" />
          </TableHead>

          <TableHead style={{ width: 79 }}>
            <FormattedMessage id="pat_table.received" />
          </TableHead>

          <TableHead style={{ width: 113 }}>
            <FormattedMessage id="pat_table.tot_payment" />
          </TableHead>

          <TableHead style={{ width: 141 }}>
            <FormattedMessage id="pat_table.status" />
          </TableHead>
        </TableRow>
      </thead>

      <tbody>
        {isLoading ? (
          <TableLoading columns={7} />
        ) : patients.length == 0 ? (
          <TableEmpty columns={7}>
            <FormattedMessage id="pat_table.patients.not_found" />
          </TableEmpty>
        ) : (
          patients.map((patient, i) => {
            const {
              latest_correction: { steps_count_completed, steps_count_total },
              precise_initial_adjust_timestamp,
              precise_latest_adjust_timestamp,
            } = patient;

            return (
              <TableRow key={i}>
                <TableCell style={{ textAlign: "center" }} className="sorting_1">
                  {patient.patient_id}

                  {precise_initial_adjust_timestamp ? (
                    <div className="precise_tip">
                      <img style={{ width: "50px", margin: "0 auto" }} src={precise} alt="3D Precise" />
                      <span className="precise_tooltip">
                        <FormattedMessage
                          id="tooltip_precise_first_comment"
                          values={{
                            labelTitle: <FormatDate value={precise_initial_adjust_timestamp} date time />,
                          }}
                        />
                        <br />
                        <FormattedMessage
                          id="tooltip_precise_last_comment"
                          values={{
                            labelTitle: <FormatDate value={precise_latest_adjust_timestamp} date time />,
                          }}
                        />
                      </span>
                    </div>
                  ) : null}
                </TableCell>

                <TableCell>
                  <NavLink to={`/pages/patient/${patient.patient_id}`}>
                    <PersonName person={patient} />
                  </NavLink>
                </TableCell>

                <TableCell>
                  <CourseName patient={patient} />
                </TableCell>

                <TableCell>
                  {!isNone(steps_count_total) && steps_count_total > 0 && !isNone(steps_count_completed) ? (
                    <span>
                      {steps_count_completed + " "}
                      <FormattedMessage id="pat_table.of" />
                      {" " + steps_count_total}
                    </span>
                  ) : null}
                </TableCell>

                <TableCell>{displayPayments ? <FormatNumber value={patient.total_payments.paid} /> : null}</TableCell>

                <TableCell>{displayPayments ? <FormatNumber value={patient.total_payments.total} /> : null}</TableCell>

                <TableCell>
                  {token && isPlanApprovalStatus(patient.latest_correction.status) ? (
                    <a
                      href={getPlanLink(patient.patient_id, token, intl.locale)}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-toggle="tooltip"
                      data-placement="top"
                      title={intl.formatMessage({ id: "tooltip.click.approve" })}
                      className="btn btn-danger"
                    >
                      <FormattedMessage id={patient.latest_correction.status} />
                      <i className="icon-action-redo" style={{ marginLeft: 5 }} />
                    </a>
                  ) : (
                    <PatientStatusBadge status={patient.latest_correction.status} index={patient.latest_correction.index} course_id={patient.course.course_id} />
                  )}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </tbody>
    </Table>
  );
}

function PageSizes({ perPage, onSelectPerPage }: { perPage: number; onSelectPerPage(newPerPage: number): void }) {
  const SIZES: { value: number; intlId: MessageDescriptor["id"] }[] = [
    { value: 50, intlId: "fifty" },
    { value: 100, intlId: "hundred" },
    { value: 200, intlId: "two.hundred" },
  ];

  return (
    <div className="col-md-offset-10 col-xs-offset-1 col-sm-offset-1">
      <ul className="pagination" id="pages">
        {SIZES.map((size) => (
          <li key={size.value}>
            <button onClick={() => onSelectPerPage(size.value)} disabled={perPage === size.value} className="btn btn-pagination" style={perPage === size.value ? { backgroundColor: "#eaecf3" } : {}}>
              <FormattedMessage id={size.intlId} />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
